import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { AudioCacheStore } from '../types';

interface AudioDB extends DBSchema {
  files: {
    key: string;
    value: Blob;
  };
}

export function createWebAudioCacheStore(): AudioCacheStore {
  let db: IDBPDatabase<AudioDB> | null = null;
  const blobUrls = new Map<string, string>();

  async function ensureDb(): Promise<IDBPDatabase<AudioDB>> {
    if (db) return db;
    db = await openDB<AudioDB>('pomodingo-audio', 1, {
      upgrade(database) {
        database.createObjectStore('files');
      },
    });
    return db;
  }

  return {
    async initialize(): Promise<void> {
      await ensureDb();
    },

    async isStorageAvailable(): Promise<boolean> {
      try {
        await ensureDb();
        return true;
      } catch (error) {
        if (error instanceof Error) {
          return error.name === 'QuotaExceededError' || error.name === 'UnknownError';
        }
        return false;
      }
    },

    async saveToCache(file: File, key: string): Promise<void> {
      const database = await ensureDb();
      await database.put('files', file, key);
    },

    async loadFromCache(key: string): Promise<string | null> {
      const database = await ensureDb();
      const blob = await database.get('files', key);
      if (!blob) return null;

      if (!blobUrls.has(key)) {
        blobUrls.set(key, URL.createObjectURL(blob));
      }
      return blobUrls.get(key) ?? null;
    },

    async getBlob(key: string): Promise<Blob | null> {
      const database = await ensureDb();
      const blob = await database.get('files', key);
      return blob ?? null;
    },

    async getAllKeys(): Promise<string[]> {
      const database = await ensureDb();
      return database.getAllKeys('files');
    },

    async deleteFromCache(key: string): Promise<void> {
      const database = await ensureDb();
      if (blobUrls.has(key)) {
        URL.revokeObjectURL(blobUrls.get(key)!);
        blobUrls.delete(key);
      }
      await database.delete('files', key);
    },

    async syncAllToServer(upload: (file: File) => Promise<unknown>): Promise<void> {
      const database = await ensureDb();
      const keys = await database.getAllKeys('files');

      for (const key of keys) {
        const blob = await database.get('files', key);
        if (!blob) continue;
        const file = new File([blob], key, { type: blob.type || 'application/octet-stream' });
        await upload(file);
      }
    },

    async clearCache(): Promise<void> {
      const database = await ensureDb();
      for (const url of blobUrls.values()) {
        URL.revokeObjectURL(url);
      }
      blobUrls.clear();
      await database.clear('files');
    },
  };
}
